import { Link } from "react-router-dom";
import { Check, FileText, Shield, BarChart3, ArrowRight, Users, BookOpen } from "lucide-react";
import logoMark from "@/assets/logo-mark.png";
import screenShot from "@/assets/dash.png";

const features = [
  {
    icon: Users,
    title: "Vendor Management",
    description:
      "Centralize all vendor information with detailed profiles, contact history, and compliance status tracking.",
  },
  {
    icon: BookOpen,
    title: "Price Books",
    description: "Create and manage comprehensive price books with line items for accurate invoice validation.",
  },
  {
    icon: FileText,
    title: "Invoice Compliance",
    description: "Automate invoice verification against price books and contracts to ensure billing accuracy.",
  },
  {
    icon: Shield,
    title: "Field Ticket Tracking",
    description: "Attach and manage field ticket PDFs to invoices for complete documentation and audit trails.",
  },
];

const benefits = [
  "Reduce invoice errors by up to 90%",
  "Automate compliance verification",
  "Centralized vendor management",
  "Complete audit trail documentation",
  "Real-time reporting and analytics",
  "Secure cloud-based platform",
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border bg-card/80 backdrop-blur-sm fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={logoMark} alt="Prolarus" className="w-9 h-9 rounded-lg" />
            <span className="text-xl font-semibold text-foreground">Prolarus</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/auth" className="btn-ghost">
              Sign In
            </Link>
            <Link to="/auth?mode=signup" className="btn-primary">
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center animate-slide-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Shield className="w-4 h-4" />
              Invoice Compliance Platform
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
              Streamline Your Invoice <span className="text-primary">Compliance</span> Process
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Prolarus helps businesses manage vendors, price books, and invoices with automated compliance
              verification. Reduce errors and save time.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/auth?mode=signup" className="btn-primary text-lg px-8 py-4">
                Start Free Trial
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/auth" className="btn-secondary text-lg px-8 py-4">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-6 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {[
              
              { value: "4 Major ERPs", label: "Integration Adapters" },
              { value: "SOC 2 Type 2", label: "Backend Data Compliance" },
              { value: "24x7x365", label: "System Support" },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Everything You Need for Invoice Compliance
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A complete solution for managing vendors, validating invoices, and maintaining compliance documentation.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="stat-card p-8 group hover:bg-card/80">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">Why Companies Choose Prolarus</h2>
              <p className="text-lg text-muted-foreground mb-8">
                Specifically designed for the unique requirements found
                in the oil and gas industry, the Prolarus platform automates and simplifies 
                invoice compliance while providing powerful tools to manage and report on 
                suppliers, price books, quotes and much more.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <Check className="w-4 h-4 text-primary" />
                    </div>
                    <span className="text-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="aspect-video rounded-xl bg-secondary border border-border flex items-center justify-center">
                {/* <BarChart3 className="w-24 h-24 text-primary/40" /> */}
                <img src={screenShot} alt="Prolarus" className="rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            Ready to Streamline Your Invoice Process?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Join hundreds of companies using Prolarus to improve their invoice compliance.
          </p>
          <Link to="/auth?mode=signup" className="btn-primary text-lg px-8 py-4">
            Get Started Today
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <img src={logoMark} alt="Prolarus" className="w-9 h-9 rounded-lg" />
            </div>
            <span className="text-lg font-semibold text-foreground">Prolarus</span>
          </div>
          <p className="text-muted-foreground text-sm">© {new Date().getFullYear()} Prolarus. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
